/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useCallback, useEffect, useRef, useState } from "react";
import "./CSS/CalendarScreen.css";
import CalendarStrip from "./Views/CalendarStrip";
import CalendarScreenHeader from "./Views/CalendarScreenHeader";
import {
  CALENDAR_RESERVATION_TABS,
  CALENDAR_RESERVATION_TYPE,
  API_DATE_FORMAT,
} from "utils/constants";
import { useStorage } from "services/StorageProvider";
import { trackListingView } from "utils/analytics";
import CalendarTableContainer from "./Views/CalendarTableContainer";
import useWSMApi from "services/api/useWSMApi";
import dayjs from "dayjs";

const CalendarScreen: FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const storedDate = sessionStorage.getItem("calendarSelectedDate");
    return storedDate ? new Date(storedDate) : new Date();
  });
  const [selectedTab, setSelectedTab] = useState<string>(() => {
    const storedTab = sessionStorage.getItem("calendarSelectedTab");
    return storedTab ?? CALENDAR_RESERVATION_TYPE.DF_WORKSPACE;
  });
  const [nextAvailableDate, setNextAvailableDate] = useState<Date | undefined>(
    undefined,
  );
  const { userProfileData } = useStorage();
  const { fetchNextAvailableDate } = useWSMApi();
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const hasAutoJumped = useRef<Set<string>>(
    (() => {
      const stored = sessionStorage.getItem("calendarAutoJumpedTabs");
      return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
    })(),
  );
  const hideCalendarStrip =
    selectedTab === CALENDAR_RESERVATION_TYPE.EVENTS ||
    selectedTab === CALENDAR_RESERVATION_TYPE.RETREAT ||
    selectedTab === CALENDAR_RESERVATION_TYPE.MB_STUDIO ||
    selectedTab === CALENDAR_RESERVATION_TYPE.WORKSHOP;

  useEffect(() => {
    const tabIndex = CALENDAR_RESERVATION_TABS.findIndex(
      (tab) => tab.value === selectedTab,
    );
    tabRefs.current[tabIndex]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedTab]);

  useEffect(() => {
    sessionStorage.setItem("calendarSelectedDate", selectedDate.toISOString());
    sessionStorage.setItem("calendarSelectedTab", selectedTab);
    trackListingView(selectedTab);
  }, [selectedDate, selectedTab]);

  const handleDateChange = (dateString: string): void => {
    setSelectedDate(new Date(dateString));
  };

  const handleNoSlots = useCallback(
    async (date: Date, tab: string) => {
      if (hasAutoJumped.current.has(tab)) return;
      hasAutoJumped.current.add(tab);
      sessionStorage.setItem(
        "calendarAutoJumpedTabs",
        JSON.stringify(Array.from(hasAutoJumped.current)),
      );
      try {
        const result = await fetchNextAvailableDate({
          date: dayjs(date).format(API_DATE_FORMAT),
          type: tab,
        });
        if (!result?.date) return;

        const next = new Date(result.date);
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        const isMonthlyTab =
          tab === CALENDAR_RESERVATION_TYPE.EVENTS ||
          tab === CALENDAR_RESERVATION_TYPE.RETREAT ||
          tab === CALENDAR_RESERVATION_TYPE.MB_STUDIO ||
          tab === CALENDAR_RESERVATION_TYPE.WORKSHOP;

        if (isMonthlyTab) {
          const limitMonth = new Date(currentYear, currentMonth + 12, 1);
          const nextMonthStart = new Date(
            next.getFullYear(),
            next.getMonth(),
            1,
          );
          if (nextMonthStart > limitMonth) return;
        } else {
          const nextAllowedMonth = new Date(currentYear, currentMonth + 1, 1);
          const lastAllowedDay = new Date(
            nextAllowedMonth.getFullYear(),
            nextAllowedMonth.getMonth() + 1,
            0,
          );
          if (next > lastAllowedDay) return;
        }

        setNextAvailableDate(next);
        setSelectedDate(next);
      } catch {}
    },
    [fetchNextAvailableDate],
  );

  return (
    <div className="wms-mobile-container">
      <div className="calendar-screen-header-sticky">
        <CalendarScreenHeader
          userProfileData={userProfileData}
          onlyMonthYearSelection={hideCalendarStrip}
          selectedDate={selectedDate}
          setSelectedDate={handleDateChange}
          nextAvailableDate={nextAvailableDate}
          selectedTab={selectedTab}
        />
        <div
          className={`calendar-strip-wrapper ${hideCalendarStrip ? "hide" : "show"}`}
        >
          <CalendarStrip
            selectedDate={selectedDate}
            setSelectedDate={handleDateChange}
            nextAvailableDate={nextAvailableDate}
          />
        </div>
        <div className="calendar-tab-container">
          {CALENDAR_RESERVATION_TABS.map((type, idx) => (
            <div
              key={type.value}
              className={`calendar-tab-container-item ${
                selectedTab === type.value ? "selected-tab" : ""
              }`}
              onClick={() => {
                setSelectedTab(type.value);
                setSelectedDate(new Date());
                setNextAvailableDate(undefined);
                hasAutoJumped.current.delete(type.value);
                sessionStorage.setItem(
                  "calendarAutoJumpedTabs",
                  JSON.stringify(Array.from(hasAutoJumped.current)),
                );
              }}
              ref={(el) => {
                tabRefs.current[idx] = el;
              }}
            >
              {type.name}
            </div>
          ))}
        </div>
      </div>
      <CalendarTableContainer
        selectedTab={selectedTab}
        selectedDate={selectedDate}
        showMonthView={hideCalendarStrip}
        onNoSlots={handleNoSlots}
      ></CalendarTableContainer>
    </div>
  );
};

export default CalendarScreen;
